import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Seo } from "@/components/seo/Seo";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";

type IncidentType = { id: string; code: string; label: string; category?: string | null };
type BookingRow = { id: string; booking_number?: string | null; scheduled_date?: string | null; companies?: { company_name?: string | null } | null; services?: { name?: string | null } | null };
type IncidentRow = {
  id: string;
  booking_id: string;
  created_at: string;
  updated_at?: string | null;
  note?: string | null;
  status: "opened" | "awaiting_vendor" | "awaiting_customer" | "resolved";
  opened_by: "customer" | "vendor" | "system";
  resolution_type?: string | null;
  resolution_note?: string | null;
  resolved_at?: string | null;
  incident_types?: { label?: string | null; code?: string | null } | null;
  bookings?: { booking_number?: string | null; scheduled_date?: string | null; services?: { name?: string | null } | null; companies?: { company_name?: string | null } | null } | null;
};
type MessageRow = { id: string; incident_id: string; author_role: string; body: string; created_at: string };

function statusVariant(s: IncidentRow["status"]) {
  if (s === "resolved") return "secondary";
  return "default";
}

export default function SupportCenter() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);

  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [types, setTypes] = useState<IncidentType[]>([]);
  const [incidents, setIncidents] = useState<IncidentRow[]>([]);

  const [selectedBookingId, setSelectedBookingId] = useState<string>("");
  const [selectedTypeId, setSelectedTypeId] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [files, setFiles] = useState<FileList | null>(null);
  const [creating, setCreating] = useState(false);

  const [activeIncidentId, setActiveIncidentId] = useState<string>("");
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [reply, setReply] = useState<string>("");
  const [sending, setSending] = useState(false);

  const activeIncident = useMemo(
    () => incidents.find((i) => i.id === activeIncidentId) || null,
    [incidents, activeIncidentId]
  );

  const statusLabel = (s: IncidentRow["status"]) => {
    switch (s) {
      case "opened": return t("support.status.opened");
      case "awaiting_vendor": return t("support.status.awaiting_vendor");
      case "awaiting_customer": return t("support.status.awaiting_customer");
      case "resolved": return t("support.status.resolved");
      default: return String(s);
    }
  };

  const roleLabel = (role: string) => {
    if (role === "vendor") return t("support.role.vendor");
    if (role === "customer") return t("support.role.customer");
    return t("support.role.system");
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const session = await supabase.auth.getSession();
        if (!session.data.session) {
          navigate("/auth");
          return;
        }

        // bookings (for picker)
        const { data: bks } = await (supabase as any)
          .from("bookings")
          .select("id, booking_number, scheduled_date, companies(company_name), services(name)")
          .order("created_at", { ascending: false })
          .limit(50);
        if (active) setBookings((bks || []) as any);

        // incident types
        const { data: tps } = await (supabase as any)
          .from("incident_types")
          .select("id, code, label, category")
          .order("category", { ascending: true })
          .order("label", { ascending: true });
        if (active) setTypes((tps || []) as any);

        // incidents list
        const { data: inc } = await (supabase as any)
          .from("booking_incidents")
          .select("id, booking_id, note, status, opened_by, resolution_type, resolution_note, resolved_at, created_at, updated_at, incident_types(label,code), bookings(booking_number,scheduled_date,services(name),companies(company_name))")
          .order("updated_at", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(100);
        if (active) {
          setIncidents((inc || []) as any);
          if (!activeIncidentId && Array.isArray(inc) && inc.length > 0) setActiveIncidentId(String((inc as any)[0].id));
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!activeIncidentId) { setMessages([]); return; }
        const { data } = await (supabase as any)
          .from("incident_messages")
          .select("id, incident_id, author_role, body, created_at")
          .eq("incident_id", activeIncidentId)
          .order("created_at", { ascending: true });
        if (active) setMessages((data || []) as any);
      } catch {
        if (active) setMessages([]);
      }
    })();
    return () => { active = false; };
  }, [activeIncidentId]);

  const uploadEvidence = async (bookingId: string, incidentId: string, list: FileList) => {
    // Use the existing private bucket used elsewhere in the app; store the storage path in booking_photos.
    const uploaded: string[] = [];
    for (const f of Array.from(list)) {
      const safeName = `${Date.now()}-${f.name}`.replace(/[^\w.\-]+/g, "_");
      const path = `${bookingId}/${incidentId}/evidence/${safeName}`;
      const up = await (supabase as any).storage.from("booking-photos").upload(path, f, { upsert: false });
      if (up.error) {
        console.error("[support] evidence upload failed", up.error);
        continue;
      }
      uploaded.push(path);
      const ins = await (supabase as any)
        .from("booking_photos")
        .insert({ booking_id: bookingId, url: path, photo_type: "evidence" });
      if (ins.error) {
        console.error("[support] booking_photos insert failed", ins.error);
      }
    }
    return uploaded;
  };

  const createIncident = async () => {
    if (!selectedBookingId || !selectedTypeId || !note.trim()) return;
    setCreating(true);
    try {
      const session = await supabase.auth.getSession();
      const uid = session.data.session?.user?.id;
      if (!uid) throw new Error("not_authenticated");

      // Determine role
      const { data: prof } = await (supabase as any).from("profiles").select("user_type").eq("user_id", uid).maybeSingle();
      const role = (prof?.user_type === "company") ? "vendor" : "customer";

      const { data: created, error } = await (supabase as any)
        .from("booking_incidents")
        .insert({
          booking_id: selectedBookingId,
          type_id: selectedTypeId,
          note: note.trim(),
          opened_by: role,
          status: "opened",
          last_actor_user_id: uid
        })
        .select("id, booking_id, created_at")
        .limit(1)
        .maybeSingle();
      if (error) throw error;

      if (files && files.length > 0) {
        const uploaded = await uploadEvidence(selectedBookingId, created.id, files);
        if (uploaded.length === 0) {
          toast({
            title: t("support.toast.photos_upload_failed_title"),
            description: t("support.toast.photos_upload_failed_desc"),
            variant: "destructive",
          });
        }
      }

      // First message
      await (supabase as any).from("incident_messages").insert({
        incident_id: created.id,
        booking_id: selectedBookingId,
        author_user_id: uid,
        author_role: role,
        body: note.trim()
      });

      // Refresh list
      const { data: inc } = await (supabase as any)
        .from("booking_incidents")
        .select("id, booking_id, note, status, opened_by, resolution_type, resolution_note, resolved_at, created_at, updated_at, incident_types(label,code), bookings(booking_number,scheduled_date,services(name),companies(company_name))")
        .order("updated_at", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(100);
      setIncidents((inc || []) as any);
      setActiveIncidentId(created.id);

      setSelectedBookingId("");
      setSelectedTypeId("");
      setNote("");
      setFiles(null);
    } finally {
      setCreating(false);
    }
  };

  const sendReply = async () => {
    if (!activeIncident || !reply.trim()) return;
    setSending(true);
    try {
      const session = await supabase.auth.getSession();
      const uid = session.data.session?.user?.id;
      if (!uid) throw new Error("not_authenticated");
      const { data: prof } = await (supabase as any).from("profiles").select("user_type").eq("user_id", uid).maybeSingle();
      const role = (prof?.user_type === "company") ? "vendor" : "customer";

      await (supabase as any).from("incident_messages").insert({
        incident_id: activeIncident.id,
        booking_id: activeIncident.booking_id,
        author_user_id: uid,
        author_role: role,
        body: reply.trim()
      });

      // Update incident status depending on who wrote last
      const nextStatus = role === "customer" ? "awaiting_vendor" : "awaiting_customer";
      await (supabase as any).from("booking_incidents").update({ status: nextStatus, last_actor_user_id: uid }).eq("id", activeIncident.id);

      const { data } = await (supabase as any)
        .from("incident_messages")
        .select("id, incident_id, author_role, body, created_at")
        .eq("incident_id", activeIncident.id)
        .order("created_at", { ascending: true });
      setMessages((data || []) as any);
      setReply("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen app-page">
      <Seo title={t("support.seo_title")} noindex canonicalPath="/support" />
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("support.title")}</h1>
            <p className="text-gray-600">{t("support.subtitle")}</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/profile")}>{t("support.back_to_profile")}</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>{t("support.new_ticket.title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={selectedBookingId} onValueChange={setSelectedBookingId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("support.new_ticket.select_booking")} />
                </SelectTrigger>
                <SelectContent>
                  {bookings.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {(b.booking_number ? `#${b.booking_number}` : b.id.slice(0, 8))} — {b.services?.name || t("support.fallback.service")} — {b.companies?.company_name || t("support.fallback.company")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("support.new_ticket.select_type")} />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("support.new_ticket.note_placeholder")} />

              <div>
                <div className="text-xs text-gray-600 mb-1">{t("support.new_ticket.attach_evidence")}</div>
                <Input type="file" multiple accept="image/*" onChange={(e) => setFiles(e.target.files)} />
              </div>

              <Button
                className="w-full bg-primary hover:bg-primary/90"
                disabled={creating || !selectedBookingId || !selectedTypeId || !note.trim()}
                onClick={createIncident}
              >
                {creating ? t("support.new_ticket.creating") : t("support.new_ticket.create")}
              </Button>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>{t("support.tickets.title")}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-sm text-gray-500">{t("support.loading")}</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    {(incidents || []).map((i) => (
                      <button
                        key={i.id}
                        onClick={() => setActiveIncidentId(i.id)}
                        className={`w-full text-left rounded-xl border p-3 hover:bg-gray-50 transition ${activeIncidentId === i.id ? "border-primary bg-white" : "border-gray-200 bg-white"}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-semibold text-gray-900 truncate">
                            {i.incident_types?.label || t("support.fallback.issue")}
                          </div>
                          <Badge variant={statusVariant(i.status) as any}>{statusLabel(i.status)}</Badge>
                        </div>
                        <div className="mt-1 text-xs text-gray-600">
                          {i.bookings?.booking_number ? `#${i.bookings.booking_number}` : i.booking_id.slice(0, 8)} — {i.bookings?.services?.name || t("support.fallback.service")} — {i.bookings?.companies?.company_name || t("support.fallback.company")}
                        </div>
                      </button>
                    ))}
                    {!incidents.length && (
                      <div className="text-sm text-gray-500">{t("support.empty_tickets")}</div>
                    )}
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-white p-3">
                    {!activeIncident ? (
                      <div className="text-sm text-gray-500">{t("support.select_ticket_hint")}</div>
                    ) : (
                      <div className="flex flex-col h-[520px]">
                        <div className="mb-2">
                          <div className="font-semibold text-gray-900">{activeIncident.incident_types?.label || t("support.ticket_title_fallback")}</div>
                          <div className="text-xs text-gray-600">
                            {t("support.ticket_meta.status")}: {statusLabel(activeIncident.status)} • {t("support.ticket_meta.booking")} {activeIncident.bookings?.booking_number ? `#${activeIncident.bookings.booking_number}` : activeIncident.booking_id.slice(0, 8)}
                          </div>
                        </div>
                        <Separator />
                        <div className="flex-1 overflow-auto py-3 space-y-2">
                          {messages.map((m) => (
                            <div key={m.id} className="text-sm">
                              <div className="text-xs text-gray-500 mb-0.5">
                                {roleLabel(m.author_role)} • {new Date(m.created_at).toLocaleString()}
                              </div>
                              <div className="rounded-lg bg-gray-50 border border-gray-200 p-2 text-gray-900 whitespace-pre-wrap">
                                {m.body}
                              </div>
                            </div>
                          ))}
                          {!messages.length && (
                            <div className="text-sm text-gray-500">{t("support.empty_messages")}</div>
                          )}
                        </div>
                        <Separator />
                        <div className="pt-3 space-y-2">
                          <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t("support.reply_placeholder")} />
                          <Button className="w-full" disabled={sending || !reply.trim()} onClick={sendReply}>
                            {sending ? t("support.sending") : t("support.send_reply")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

